'use client'

import {PhoneIcon} from '@heroicons/react/24/outline'
import {z} from "zod";
import {FieldErrors, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {CustomerServiceRequestSchema} from "@/lib/formValidators/contractForms/ContactUsFrom";
import {useRouter} from "next/navigation";
import React, {useState} from "react";
import {createQuickQuote} from "@/lib/api/apiPosts/quote/quoteProductsPostAPI";
import {createCustomerServiceRequest} from "@/lib/api/apiPosts/customer/customerServiceRequestPostAPI";
import {usStates} from "@/modules/data/countriesAndStates";

export default function ContactUsComponent() : JSX.Element{
    const router = useRouter()
    const [showProducts, setShowProducts] = useState(false);
    const [defaultLoaded, setDefaultLoaded] = useState(false);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);

    type formData = z.infer<typeof CustomerServiceRequestSchema>;
    const {
        setValue,
        register,
        handleSubmit,
        reset,
        formState: {errors},
    } = useForm<formData>({
        resolver: zodResolver(CustomerServiceRequestSchema)
    });

    if (!defaultLoaded) {
        reset( {tenantUuid: 'BRYA', pageSourceTypeCode: 'CONTACT_US_FORM', preferredContactMethod: 'CALL', nameUnknownFlag: false, quoteTypeAutoFlag: false, quoteTypeHomeFlag: false,quoteTypeHealthFlag: false, quoteTypeLifeFlag: false, quoteTypeCycleFlag: false, quoteTypeBoatFlag: false, quoteTypeCommercialFlag: false, quoteTypeOtherFlag: false, sr22Flag: false })
        setDefaultLoaded(true);
    }

    const submitData = (data: formData) => {
        setIsFormSubmitting(true);
        const quoteTypeAutoCount= data.quoteTypeAutoFlag? 1: 0;
        const quoteTypeBoatCount= data.quoteTypeBoatFlag? 1: 0;
        const quoteTypeCommercialCount= data.quoteTypeCommercialFlag? 1: 0;
        const quoteTypeCycleCount= data.quoteTypeCycleFlag? 1: 0;
        const quoteTypeHealthCount= data.quoteTypeHealthFlag? 1: 0;
        const quoteTypeHomeCount= data.quoteTypeHomeFlag? 1: 0;
        const quoteTypeLifeCount= data.quoteTypeLifeFlag? 1: 0;
        const quoteTypeOtherCount= data.quoteTypeOtherFlag? 1: 0;
        const quoteTypeTotalCount= quoteTypeAutoCount + quoteTypeBoatCount + quoteTypeCommercialCount + quoteTypeCycleCount + quoteTypeHealthCount + quoteTypeHomeCount + quoteTypeLifeCount + quoteTypeOtherCount;

        if (data.requestTypeCode === 'QUOTE') {
            // This is a quote request type, we will create a quote
            createQuickQuote(data).then((value) => {

                if (value && value.validated) {
                    //Trigger GTM Event
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                        event: 'gtm.formSubmit',
                        formId: "form-contact-us",
                        form_name: 'form-contact-us', // Add relevant details as needed
                        form_id: "form-contact-us",
                        form_destination: window.location.href,
                        form_length: 18,
                        quoteTypeAutoCount: quoteTypeAutoCount,
                        quoteTypeBoatCount: quoteTypeBoatCount,
                        quoteTypeCommercialCount: quoteTypeCommercialCount,
                        quoteTypeCycleCount: quoteTypeCycleCount,
                        quoteTypeHealthCount: quoteTypeHealthCount,
                        quoteTypeHomeCount: quoteTypeHomeCount,
                        quoteTypeLifeCount: quoteTypeLifeCount,
                        quoteTypeOtherCount: quoteTypeOtherCount,
                        quoteTypeTotalCount: quoteTypeTotalCount,
                        requestTypeCode: data.requestTypeCode,
                        regionCity: data.addressRegionIsoCode + ':' + data.addressCity.toUpperCase(),
                        eventCallback: function() {
                            let queryString = '?qid=';
                            queryString = queryString + encodeURIComponent(value.quickQuoteId);
                            queryString = queryString + '&quuid='
                            queryString = queryString + encodeURIComponent(value.quickQuoteUuid);
                            router.push("/quote/quick-quote/confirmation"+queryString);
                        }
                    });
                    //Redirect
                }
            }).catch(err=>{
                let errorMessageElement = document.getElementById('pageErrorMessage')
                if (errorMessageElement) {
                    errorMessageElement.innerText = "An expected error occurred when saving request";
                    errorMessageElement.hidden = false;
                    errorMessageElement.focus();
                }
                console.log(err)
                setIsFormSubmitting(false);
            })
        } else {
            createCustomerServiceRequest(data).then((value) => {

                if (value && value.validated) {
                    //Trigger GTM Event
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                        event: 'gtm.formSubmit',
                        formId: "form-contact-us",
                        form_name: 'form-contact-us', // Add relevant details as needed
                        form_id: "form-contact-us",
                        form_destination: window.location.href,
                        form_length: 18,
                        quoteTypeAutoCount: quoteTypeAutoCount,
                        quoteTypeBoatCount: quoteTypeBoatCount,
                        quoteTypeCommercialCount: quoteTypeCommercialCount,
                        quoteTypeCycleCount: quoteTypeCycleCount,
                        quoteTypeHealthCount: quoteTypeHealthCount,
                        quoteTypeHomeCount: quoteTypeHomeCount,
                        quoteTypeLifeCount: quoteTypeLifeCount,
                        quoteTypeOtherCount: quoteTypeOtherCount,
                        quoteTypeTotalCount: quoteTypeTotalCount,
                        requestTypeCode: data.requestTypeCode,
                        regionCity: data.addressRegionIsoCode + ':' + data.addressCity.toUpperCase(),
                        preferredContactMethod: data.preferredContactMethod,
                        eventCallback: function() {
                            let queryString = '?id=';
                            queryString = queryString + encodeURIComponent(value.serviceRequestId);
                            queryString = queryString + '&uuid='
                            queryString = queryString + encodeURIComponent(value.serviceRequestUuid);
                            router.push("/service/contact-us/confirmation"+queryString);
                        }
                    });
                    //Redirect
                }
            }).catch(err=>{
                let errorMessageElement = document.getElementById('pageErrorMessage')
                if (errorMessageElement) {
                    errorMessageElement.innerText = "An expected error occurred when saving request";
                    errorMessageElement.hidden = false;
                    errorMessageElement.focus();
                }
                setIsFormSubmitting(false);
        })
        }

    }
    const onError = (errors: FieldErrors<formData>) => {
        console.log("Form Errors", errors)
        let errorMessageElement = document.getElementById('pageErrorMessage')
        if (errorMessageElement && errors.formErrors?.message) {
            errorMessageElement.innerText = errors.formErrors?.message;
            errorMessageElement.hidden = false;
        }
    }

    const handleRequestTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setShowProducts(event.target.value === "QUOTE");
        setValue('requestTypeCode', event.target.value, { shouldValidate: true })
    };

    return (

            <div className="space-y-10 divide-y divide-gray-900/10 bg-gray-100 p-2">

                <div className="grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3">
                    <div className="px-4 sm:px-0">
                        <h2 className="text-base font-semibold leading-7 text-gray-900">Submit an on-line request</h2>
                        <p className="mt-1 text-sm leading-6 text-gray-600">Complete our web form and we will route your
                            request to our service department for a timeline response.</p>

                        <p className="mt-1 text-xs leading-6 text-gray-600 pt-4">
                            If you prefer to speak to an agent or customer service, give us a call.
                        </p>

                        <div className="flex items-center p-2">
                            <div className="text-center text-gray-900">
                                <div className="flex items-center">
                                    <div className="flex-none">
                                        <a href="tel:2173555555" id='clickToCall'>
                                            <PhoneIcon className="mx-2 h-8 w-8 text-gray-900" aria-hidden="true" title={'Phone Icon'}/>
                                        </a>
                                    </div>
                                    <div className="flex-auto text-left text-xs">
                                        <table>
                                            <tbody>
                                            <tr>
                                                <td className="pr-2">
                                                    Phone:
                                                </td>
                                                <td>
                                                    <a href="tel:2173555555"
                                                       className="underline font-bold">217-355-5555</a>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="pr-2">
                                                    M – F:
                                                </td>
                                                <td>
                                                    9am – 5pm CST
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="pr-2">
                                                    Sat:
                                                </td>
                                                <td>
                                                    9am – 12pm CST
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <form id={"form-contact-us"} onSubmit={handleSubmit(submitData, onError)}
                          className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2">
                        <div className="px-4 py-6 sm:p-8">
                            <p id={"pageErrorMessage"} className="text-red-500 text-lg italic pt-1"
                               hidden={true}></p>
                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                                <div className="sm:col-span-3">
                                    <label htmlFor="firstName"
                                           className=" text-sm font-medium leading-6 text-gray-900">
                                        First name
                                    </label>
                                    <span className="text-red-500 text-xs italic pl-1">*</span>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            id="firstName"
                                            autoComplete="given-name"
                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            {...register('firstName')}
                                        />
                                        {errors.firstName?.message &&
                                            <p className="text-red-500 text-xs italic pt-1">{errors.firstName?.message.toString()}</p>}
                                    </div>
                                </div>

                                <div className="sm:col-span-3">
                                    <label htmlFor="lastName" className="text-sm font-medium leading-6 text-gray-900">
                                        Last name
                                    </label>
                                    <span className="text-red-500 text-xs italic pl-1">*</span>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            id="lastName"
                                            autoComplete="family-name"
                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            {...register('lastName')}
                                        />
                                        {errors.lastName?.message &&
                                            <p className="text-red-500 text-xs italic pt-1">{errors.lastName?.message.toString()}</p>}
                                    </div>
                                </div>

                                <div className="col-span-full">
                                    <label htmlFor="primaryEmailAddress"
                                           className="text-sm font-medium leading-6 text-gray-900">
                                        Email address
                                    </label>
                                    <span className="text-red-500 text-xs italic pl-1">*</span>
                                    <div className="mt-2">
                                        <input
                                            id="primaryEmailAddress"
                                            type="email"
                                            autoComplete="email"
                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            {...register('primaryEmailAddress')}
                                        />
                                        {errors.primaryEmailAddress?.message &&
                                            <p className="text-red-500 text-xs italic pt-1">{errors.primaryEmailAddress?.message.toString()}</p>}
                                    </div>
                                </div>

                                <div className="col-span-full">
                                    <label htmlFor="addressLine1"
                                           className="text-sm font-medium leading-6 text-gray-900">
                                        Street address
                                    </label>
                                    <span className="text-red-500 text-xs italic pl-1">*</span>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            id="addressLine1"
                                            autoComplete="street-address"
                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            {...register('addressLine1')}
                                        />
                                        {errors.addressLine1?.message &&
                                            <p className="text-red-500 text-xs italic pt-1">{errors.addressLine1?.message.toString()}</p>}

                                    </div>
                                </div>

                                <div className="sm:col-span-2 sm:col-start-1">
                                    <label htmlFor="city" className="text-sm font-medium leading-6 text-gray-900">
                                        City
                                    </label>
                                    <span className="text-red-500 text-xs italic pl-1">*</span>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            id="addressCity"
                                            autoComplete="address-level2"
                                            className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            {...register('addressCity')}
                                        />
                                        {errors.addressCity?.message &&
                                            <p className="text-red-500 text-xs italic pt-1">{errors.addressCity?.message.toString()}</p>}

                                    </div>
                                </div>

                                <div className="sm:col-span-2">
                                    <label htmlFor="addressRegionIsoCode"
                                           className="text-sm font-medium leading-6 text-gray-900">
                                        State / Province
                                    </label>
                                    <span className="text-red-500 text-xs italic pl-1">*</span>
                                    <div className="mt-2">
                                        <select
                                            id="addressRegionIsoCode"
                                            autoComplete="address-level1"
                                            className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            {...register('addressRegionIsoCode')}
                                        >
                                            <option value="">--Select a state--</option>
                                            {usStates.map((state) => (
                                                <option key={state.value} value={state.value}>
                                                    {state.label}
                                                </option>))}
                                        </select>
                                        {errors.addressRegionIsoCode?.message &&
                                            <p className="text-red-500 text-xs italic pt-1">{errors.addressRegionIsoCode?.message.toString()}</p>}
                                    </div>
                                </div>

                                <div className="sm:col-span-2">
                                    <label htmlFor="addressPostalCode"
                                           className="text-sm font-medium leading-6 text-gray-900">
                                        ZIP / Postal code
                                    </label>
                                    <span className="text-red-500 text-xs italic pl-1">*</span>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            id="addressPostalCode"
                                            autoComplete="postal-code"
                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            {...register('addressPostalCode')}
                                        />
                                        {errors.addressPostalCode?.message &&
                                            <p className="text-red-500 text-xs italic pt-1">{errors.addressPostalCode?.message.toString()}</p>}

                                    </div>
                                </div>

                                <div className="col-span-full">
                                    <label htmlFor="phone-number"
                                           className="text-sm font-medium leading-6 text-gray-900">
                                        Phone Number
                                    </label>
                                    <span className="text-red-500 text-xs italic pl-1">*</span>
                                    <div className="relative mt-2 rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 flex items-center">
                                            <label htmlFor="primaryPhone" className="sr-only">
                                                Country
                                            </label>
                                            <select
                                                id="country"
                                                name="country"
                                                autoComplete="country"
                                                className="h-full rounded-md border-0 bg-transparent py-0 pl-3 pr-7 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                            >
                                                <option>US</option>
                                            </select>
                                        </div>
                                        <input
                                            type="text"
                                            id="primaryPhone"
                                            className="block w-full rounded-md border-0 py-1.5 pl-16 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            placeholder="(555) 555-5555"
                                            {...register('primaryPhone')}
                                        />
                                    </div>
                                    {errors.primaryPhone?.message &&
                                        <p className="text-red-500 text-xs italic pt-1">{errors.primaryPhone?.message.toString()}</p>}

                                </div>
                                <div className="col-span-full">
                                    <label htmlFor="preferredContactMethod"
                                           className=" text-sm font-medium leading-6 text-gray-900">
                                        Preferred Contact Method
                                    </label>
                                    <span className="text-red-500 text-xs italic pl-1">*</span>
                                    <div className="mt-2">
                                        <select
                                            id="preferredContactMethod"
                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600  sm:text-sm sm:leading-6"
                                            {...register('preferredContactMethod')}
                                        >
                                            <option value={""}>- select -</option>
                                            <option value={"CALL"}>Give me a Call</option>
                                            <option value={"TEXT"}>Send me a Text</option>
                                            <option value={"EMAIL"}>Send me an Email</option>
                                        </select>
                                        {errors.preferredContactMethod?.message &&
                                            <p className="text-red-500 text-xs italic pt-1">{errors.preferredContactMethod?.message.toString()}</p>}
                                    </div>
                                </div>

                                <div className="col-span-full">
                                    <label htmlFor="requestType"
                                           className=" text-sm font-medium leading-6 text-gray-900">
                                        Type of Request
                                    </label>
                                    <span className="text-red-500 text-xs italic pl-1">*</span>
                                    <div className="mt-2">
                                        <select
                                            id="requestTypeCode"
                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600  sm:text-sm sm:leading-6"
                                            {...register('requestTypeCode')}
                                            onChange={handleRequestTypeChange}
                                        >
                                            <option value={""}>- select -</option>
                                            <option value={"QUOTE"}>Get an Insurance Quote</option>
                                            <option value={"POLICY_QUESTION"}>Question on existing policy</option>
                                            <option value={"POLICY_ENDORSEMENT"}>Make a change on existing policy
                                            </option>
                                            <option value={"POLICY_PAYMENT"}>Make a payment</option>
                                        </select>
                                        {errors.requestTypeCode?.message &&
                                            <p className="text-red-500 text-xs italic pt-1">{errors.requestTypeCode?.message.toString()}</p>}
                                    </div>
                                </div>
                                <div id={'quote-product-categories'}
                                     className={`space-y-5 col-span-full ${showProducts ? '' : 'hidden'}`}>
                                    <div className="font-medium text-gray-900">Select the type(s) of quotes you are
                                        interested in.
                                    </div>
                                    {errors.formErrors?.message &&
                                        <p className="text-red-500 text-xs italic pt-1">{errors.formErrors?.message.toString()}</p>}
                                    {/* AUTO */}
                                    <div className="relative flex items-start">
                                        <div className="flex h-6 items-center">
                                            <input
                                                id="quoteTypeAutoFlag"
                                                type="checkbox"
                                                aria-describedby="comments-description"
                                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                {...register('quoteTypeAutoFlag')}
                                            />
                                        </div>
                                        <div className="ml-3 text-sm leading-6">
                                            <label htmlFor="comments" className="font-medium text-gray-900">
                                                Auto Insurance
                                            </label>
                                            <p id="comments-description" className="text-gray-500">
                                                Protection for your car(s) from accident, theft, fire, or vandalism.
                                            </p>
                                        </div>
                                    </div>
                                    {/* HOME */}
                                    <div className="relative flex items-start">
                                        <div className="flex h-6 items-center">
                                            <input
                                                id="quoteTypeHomeFlag"
                                                type="checkbox"
                                                aria-describedby="comments-description"
                                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                {...register('quoteTypeHomeFlag')}
                                            />
                                        </div>
                                        <div className="ml-3 text-sm leading-6">
                                            <label htmlFor="comments" className="font-medium text-gray-900">
                                                Home or Renters Insurance
                                            </label>
                                            <p id="comments-description" className="text-gray-500">
                                                Insurance for your home and belongings whether you are a home owner or
                                                renter.
                                            </p>
                                        </div>
                                    </div>
                                    {/* LIFE */}
                                    <div className="relative flex items-start">

                                        <div className="flex h-6 items-center">
                                            <input
                                                id="quoteTypeLifeFlag"
                                                type="checkbox"
                                                aria-describedby="comments-description"
                                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                {...register('quoteTypeLifeFlag')}
                                            />
                                        </div>
                                        <div className="ml-3 text-sm leading-6">
                                            <label htmlFor="comments" className="font-medium text-gray-900">
                                                Life Insurance
                                            </label>
                                            <p id="comments-description" className="text-gray-500">
                                                Protection and financial security for your family and loved ones.
                                            </p>
                                        </div>
                                    </div>
                                    {/* MOTORCYCLE */}
                                    <div className="relative flex items-start">

                                        <div className="flex h-6 items-center">
                                            <input
                                                id="quoteTypeCycleFlag"
                                                type="checkbox"
                                                aria-describedby="comments-description"
                                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                {...register('quoteTypeCycleFlag')}
                                            />
                                        </div>
                                        <div className="ml-3 text-sm leading-6">
                                            <label htmlFor="comments" className="font-medium text-gray-900">
                                                Motorcycle Insurance
                                            </label>
                                            <p id="comments-description" className="text-gray-500">
                                                Protection for your motorcycle(s) from accident, theft, fire, or
                                                vandalism.
                                            </p>
                                        </div>
                                    </div>
                                    {/* COMMERCIAL */}
                                    <div className="relative flex items-start">

                                        <div className="flex h-6 items-center">
                                            <input
                                                id="quoteTypeCommercialFlag"
                                                type="checkbox"
                                                aria-describedby="comments-description"
                                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                {...register('quoteTypeCommercialFlag')}
                                            />
                                        </div>
                                        <div className="ml-3 text-sm leading-6">
                                            <label htmlFor="comments" className="font-medium text-gray-900">
                                                Commercial Insurance
                                            </label>
                                            <p id="comments-description" className="text-gray-500">
                                                Insurance protection for your business.
                                            </p>
                                        </div>
                                    </div>
                                    {/* OTHER */}
                                    <div className="relative flex items-start">

                                        <div className="flex h-6 items-center">
                                            <input
                                                id="quoteTypeOtherFlag"
                                                type="checkbox"
                                                aria-describedby="comments-description"
                                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                {...register('quoteTypeOtherFlag')}
                                            />
                                        </div>
                                        <div className="ml-3 text-sm leading-6">
                                            <label htmlFor="quoteTypeOtherFlag" className="font-medium text-gray-900">
                                                Other Insurance
                                            </label>
                                            <p id="comments-description" className="text-gray-500">
                                                Insurance for your ATV, Boat, Golf Cart, Personal Umbrella, Mobile Home,
                                                RV, Trailer.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-span-full">
                                    <label htmlFor="comment"
                                           className="block text-sm font-medium leading-6 text-gray-900">
                                        Add your comment
                                    </label>
                                    <div className="mt-2">
                                    <textarea
                                        rows={4}
                                        id="customerComments"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                        defaultValue={''}
                                        {...register('customerComments')}
                                    />
                                    </div>
                                </div>
                                <div className="col-span-full">
                                    <div className="text-red-500 text-xs italic inline-block">*</div>
                                    <span
                                        className="text-gray-900 text-xs italic pl-2">Denotes required field</span>
                                </div>
                            </div>
                        </div>
                        <div
                            className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
                        {!isFormSubmitting && (
                                <button id="btn-cancel" type="button"
                                        className="text-sm font-semibold leading-6 text-gray-900">
                                    Cancel
                                </button>)}
                            {!isFormSubmitting && (
                                <button id="btn-submit"
                                        type="submit"
                                        className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                >
                                    Submit
                                </button>)}
                            {isFormSubmitting && (
                                <button id="btn-processing" type="button"
                                        className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 flex items-center"
                                        disabled>
                                    <svg className="animate-spin h-5 w-5 text-white mr-2"
                                         xmlns="http://www.w3.org/2000/svg"
                                         fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                                stroke-width="4"></circle>
                                        <path className="opacity-75" fill="currentColor"
                                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Processing...
                                </button>)}
                        </div>
                    </form>
                </div>
            </div>

    )
}